import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  Target,
  Question,
  CaretDown,
  Clock,
  Lightning,
  Activity,
  Timer,
  Prohibit,
  ShieldWarning,
  Heart,
  ListChecks,
  Eye
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const OvertradingPT = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Overtrading: O Que É e Como Evitar | Guia Completo',
      description: 'Entenda o que é overtrading, por que acontece, e técnicas práticas para evitar esse erro que destrói contas de trading.',
      path: '/pt/overtrading',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/overtrading'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "Quantos trades por dia é overtrading?",
      answer: "Não existe um número fixo — depende do seu estilo e mercado. O problema não é a quantidade absoluta, mas operar mais do que seu plano permite ou operar sem setup válido. Um scalper pode fazer 20 trades/dia dentro do plano. Um swing trader pode fazer 2 trades/dia e estar em overtrading se não houver setup."
    },
    {
      question: "Como sei se estou em overtrading?",
      answer: "Sinais claros: você opera fora do horário planejado, entra em trades sem setup claro, aumenta frequência após losses para 'recuperar', sente urgência de estar sempre posicionado, sua mente não para de pensar em 'oportunidades'. Se 3+ desses se aplicam, você provavelmente está em overtrading."
    },
    {
      question: "Overtrading é um problema emocional ou técnico?",
      answer: "Ambos, mas a raiz geralmente é emocional. A necessidade de ação, medo de perder oportunidades (FOMO), desejo de recuperar losses — tudo isso são gatilhos emocionais. A solução técnica (limites de trades) só funciona se você resolver a causa emocional também."
    },
    {
      question: "Limitar trades por dia funciona?",
      answer: "É um bom começo, mas não resolve sozinho. Se você se força a parar no 5º trade mas ainda sente a urgência de operar, o problema continua. O ideal é combinar limite técnico com trabalho na mentalidade — entender que menos trades geralmente significa mais lucro."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Começar Grátis
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-4">
            <ShieldWarning size={16} weight="fill" />
            <span>Gestão de Risco</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Overtrading: O Destruidor Silencioso de Contas
          </h1>
          <p className="text-lg text-white/60">
            Como identificar e eliminar o excesso de operações
          </p>
        </header>

        <div className="bg-gradient-to-r from-red-500/10 to-transparent border-l-2 border-red-500 p-6 rounded-r-lg mb-12">
          <p className="text-white/80 leading-relaxed">
            Overtrading é operar além do necessário — mais trades do que seu plano permite, 
            fora do horário ideal, sem setup válido. É um dos erros mais comuns e mais 
            destrutivos no trading. Não porque cada trade seja ruim, mas porque o acúmulo 
            de custos, erros emocionais e decisões ruins eventualmente destroem o capital.
          </p>
        </div>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <Warning size={20} className="text-red-400" />
            </div>
            <h2 className="text-2xl font-bold">Por Que Overtrading Acontece</h2>
          </div>
          
          <p className="text-white/70 leading-relaxed mb-6">
            A raiz do overtrading raramente é técnica — é psicológica. Entender as causas 
            é o primeiro passo para resolver.
          </p>

          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Heart size={20} className="text-red-400" />
                <h3 className="font-semibold text-white">Adrenalina e Dopamina</h3>
              </div>
              <p className="text-white/60 leading-relaxed">
                Trading ativa os mesmos circuitos cerebrais do jogo. A expectativa de um trade 
                vencedor libera dopamina. Seu cérebro quer mais — mesmo quando sua conta não aguenta.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Lightning size={20} className="text-amber-400" />
                <h3 className="font-semibold text-white">FOMO (Fear of Missing Out)</h3>
              </div>
              <p className="text-white/60 leading-relaxed">
                O mercado está se movendo e você não está posicionado. A sensação de estar 
                "perdendo dinheiro" por não estar no mercado é poderosa — mas ilusória. 
                Estar de fora não é perder.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <TrendUp size={20} className="text-blue-400" />
                <h3 className="font-semibold text-white">Revenge Trading</h3>
              </div>
              <p className="text-white/60 leading-relaxed">
                Você perdeu. Agora quer "recuperar". Aumenta a frequência, o tamanho, a agressividade. 
                O mercado não se importa com seu loss anterior. Mas seu emocional corrompido agora 
                toma decisões piores.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Brain size={20} className="text-purple-400" />
                <h3 className="font-semibold text-white">Tédio e Ansiedade</h3>
              </div>
              <p className="text-white/60 leading-relaxed">
                Você está na frente do computador. O mercado está parado. Seu setup não aparece. 
                Mas você "precisa" fazer algo. Então inventa um trade. Isso é overtrading por tédio.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Activity size={20} className="text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold">Sinais de Que Você Está em Overtrading</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <CheckCircle size={18} className="text-amber-400 mb-2" />
              <p className="text-white/70 text-sm">Opera fora do horário que planejou</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <CheckCircle size={18} className="text-amber-400 mb-2" />
              <p className="text-white/70 text-sm">Entra em trades sem setup claro definido</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <CheckCircle size={18} className="text-amber-400 mb-2" />
              <p className="text-white/70 text-sm">Sente urgência de "estar posicionado"</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <CheckCircle size={18} className="text-amber-400 mb-2" />
              <p className="text-white/70 text-sm">Aumenta trades após uma sequência de perdas</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <CheckCircle size={18} className="text-amber-400 mb-2" />
              <p className="text-white/70 text-sm">Seus custos de corretagem estão altos demais</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <CheckCircle size={18} className="text-amber-400 mb-2" />
              <p className="text-white/70 text-sm">Sua mente não para de pensar em trades</p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Prohibit size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Como Eliminar Overtrading</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">1</div>
                <h3 className="font-semibold text-white">Defina Limites Diários Claros</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Número máximo de trades, loss máximo diário, horário de operação. 
                Escreva essas regras. Cole na tela. Respeite sem exceção.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">2</div>
                <h3 className="font-semibold text-white">Registre Todos os Trades</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Um diário de trades revela padrões que você não vê. Quantos trades por dia? 
                Após quantos trades consecutivos sua performance cai? 
                Dados objetivos são mais convincentes que intuição.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">3</div>
                <h3 className="font-semibold text-white">Implemente "Cooling Off" Periods</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Após um loss, espere 10-15 minutos antes do próximo trade. 
                Após 2 losses seguidos, pare por 1 hora ou pelo resto do dia. 
                O objetivo é quebrar o ciclo emocional.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">4</div>
                <h3 className="font-semibold text-white">Questione Cada Trade</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Antes de entrar, pergunte: "Este trade está no meu plano?" 
                "Eu operaria isso se fosse meu único trade da semana?" 
                Se a resposta for não, não entre.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">5</div>
                <h3 className="font-semibold text-white">Trabalhe a Mentalidade</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Entenda que não operar também é uma decisão de trading. 
                Os melhores traders passam mais tempo fora do mercado do que dentro. 
                Paciência é literalmente lucrativa.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Target size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">O Custo Real do Overtrading</h2>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
            <p className="text-white/70 leading-relaxed mb-4">
              Vamos fazer as contas. Suponha que você faz 10 trades extras por semana que não 
              deveria. Cada um custa R$20 em spread/corretagem.
            </p>
            <ul className="space-y-2 text-white/60">
              <li>• <strong className="text-white">Por semana:</strong> 10 × R$20 = R$200</li>
              <li>• <strong className="text-white">Por mês:</strong> R$800</li>
              <li>• <strong className="text-white">Por ano:</strong> R$9.600</li>
            </ul>
            <p className="text-white/70 leading-relaxed mt-4">
              Isso sem contar os losses nos trades ruins. Em um ano, overtrading pode 
              custar mais que seu lucro bruto.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Question size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Perguntas Frequentes</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-white/10 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  <CaretDown 
                    size={20} 
                    className={`text-white/60 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-white/70 leading-relaxed border-t border-white/5 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#00E676]/10 via-transparent to-transparent border border-[#00E676]/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4">Monitore Seu Comportamento</h2>
            <p className="text-white/70 mb-6 leading-relaxed">
              GENI mostra quantos trades você faz por dia e como sua performance 
              muda com a frequência. Dados são o primeiro passo para mudança.
            </p>
            <Link 
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00E676] text-black font-semibold rounded-lg hover:bg-[#00E676]/90 transition-colors"
            >
              Começar Grátis
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">Artigos Relacionados</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link 
              to="/pt/por-que-traders-nao-evoluem"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Por Que Traders Não Evoluem
              </h3>
              <p className="text-sm text-white/50">Os erros que impedem o progresso</p>
            </Link>
            <Link 
              to="/pt/como-melhorar-no-day-trade"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Como Melhorar no Day Trade
              </h3>
              <p className="text-sm text-white/50">Técnicas práticas baseadas em dados</p>
            </Link>
          </div>
        </section>

      </article>

      <footer className="border-t border-white/10 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center">
              <img 
                src={LOGO_HORIZONTAL}
                alt="GENI" 
                className="h-8 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-white/30">© 2025 GENI</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OvertradingPT;
